import { useCallback, useEffect, useRef, useState } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { ConfluenceBackground } from "@/components/signup/confluence-background";
import {
    UfoCustomTimingKey,
    UfoCustomTimingWrapper,
    ufoEventRedirectToProduct
} from "@/components/signup/utils";
import { trackPerceivedProvisioningEvent } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/analytics.ts";

import { useSignupProvisioningContext } from "../signup-provisioning-context.tsx";

import { LoadingAnimation } from "./loading-animation";

export const ConfluenceLayout = () => {
    const { status, createRedirectUrl } = useSignupProvisioningContext();

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [firedProvisioningCompleteEvent, setFiredProvisioningCompleteEvent] =
        useState<boolean>(false);

    // Start analytics timer for time spent on provisioning (perceived provisioning)
    const provisioningStartTimestamp = useRef<number>(Date.now() / 1000);

    const fireProvisioningTimeSpentEvent = useCallback(() => {
        const endTimestamp = Date.now() / 1000;
        const startTimestamp = provisioningStartTimestamp.current ?? 0;
        const time = Math.round(endTimestamp - startTimestamp);

        createAnalyticsEvent({
            eventType: "track",
            action: "viewed",
            actionSubject: "provisioningScreen",
            attributes: { time },
            containerId: "provisioningPage"
        }).fire();
    }, [createAnalyticsEvent]);

    useEffect(() => {
        if (!provisioningStartTimestamp.current) {
            provisioningStartTimestamp.current = Date.now() / 1000;
        }
        /* istanbul ignore next */
        const waitForProvisioningToComplete = async () => {
            if (status !== "success") return;

            const redirectUrl = createRedirectUrl();
            // SLO "good event" for redirect into product
            ufoEventRedirectToProduct.success({
                customData: {
                    reactComponent: "ConfluenceLayout default",
                    analyticsEvent: "redirected",
                    redirectFrom: "confirmationPage",
                    provisioningStatus: status
                }
            });

            if (!firedProvisioningCompleteEvent) {
                fireProvisioningTimeSpentEvent();
                trackPerceivedProvisioningEvent({
                    createAnalyticsEvent,
                    productKey: "confluence",
                    finalProvisioningScreen: "provisioning-screen",
                    startTimeSeconds: provisioningStartTimestamp.current
                });
            }
            setFiredProvisioningCompleteEvent(true);

            window.location.assign(redirectUrl);
        };
        waitForProvisioningToComplete();
    }, [
        createRedirectUrl,
        status,
        fireProvisioningTimeSpentEvent,
        createAnalyticsEvent,
        firedProvisioningCompleteEvent,
        provisioningStartTimestamp
    ]);

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={
                UfoCustomTimingKey.CONFLUENCE_ONBOARDING_SPINNER_DWELL_TIME
            }
        >
            <ConfluenceBackground />
            <LoadingAnimation isLoadingComplete={status === "success"} />
        </UfoCustomTimingWrapper>
    );
};
